import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../api";

import Work from "./Work";
import About from "./About";
import LoadingAnimation from "../components/LoadingAnimation.js";

import { styleThresholds } from "../resources/styleThresholds";
import CheckStyleThreshold from "../hooks/checkStyleThreshold";

function Page({
  isWork,
  setAppTransitionClass,
  setAppWorkColours,
  onAppTransitionEnd,
}) {
  const [workInfo, setWorkInfo] = useState(null);
  const [lastProjectIndex, setLastProjectIndex] = useState(null);

  const gridRef = useRef();
  const [gridStyleRows, setGridStyleRows] = useState({});
  const [gridNRows, setGridNRows] = useState(13);
  const [gridStyleColumns, setGridStyleColumns] = useState({});
  const gridGapValue = 24;

  const [gridIsAnimating, setGridIsAnimating] = useState(false);
  const [pageIsExiting, setPageIsExiting] = useState(false);
  const [pageIsEntering, setPageIsEntering] = useState(false);

  const [dragStart, setDragStart] = useState({});
  const [dragCell, setDragCell] = useState({});
  const [gridTemplateStart, setGridTemplateStart] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    const fetchURL = API_URL + `projects/`;

    const fetchData = async () => {
      fetch(fetchURL)
        .then((response) => response.json())
        .then((result) => {
          setWorkInfo(result);
        })
        .catch((error) => {
          console.error("Fetch error:", error.message);
          console.error("Error details:", error);
        });
    };

    if (typeof window !== "undefined") {
      fetchData();
    }
  }, []);


  useEffect(() => {
    if (pageIsEntering || pageIsExiting) return;
    if (isWork) {
      setAppTransitionClass("enterWork");
      setPageIsEntering(true);
    } else {
      setAppTransitionClass("enterAbout");
      setPageIsEntering(true);
    }
  }, []);

  let previousGridHeight = 0,
    previousGridWidth = 0;

  function debounce(func, timeout = 100) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  }

  const setStartingGridNRows = () => {
    const grid = gridRef.current;

    const divStyles = window.getComputedStyle(grid);

    const templateRows = divStyles
      .getPropertyValue("grid-template-rows")
      .split(" ");

    setGridNRows(templateRows.length);
  };

  const updateGridValues = debounce(() => {
    if (CheckStyleThreshold()) {
      setGridStyleRows({});
      setGridStyleColumns({});
      return;
    }

    const grid = gridRef.current;

    if (!grid) return;

    const gridWidth = grid.offsetWidth;
    const gridHeight = grid.offsetHeight;

    if (previousGridHeight === 0 || previousGridWidth === 0) {
      previousGridHeight = gridHeight;
      previousGridWidth = gridWidth;
      return;
    }
    setGridIsAnimating(true);
    const divStyles = window.getComputedStyle(grid);

    //if height changed
    if (previousGridHeight !== gridHeight) {
      if (!gridNRows) {
        setStartingGridNRows();
      }

      const templateRows = divStyles
        .getPropertyValue("grid-template-rows")
        .split(" ")
        .map((row) => parseFloat(row.replace("px", "")))
        .slice(0, gridNRows); //filter because too many draggable cells is creating new row

      const rowsGapSum = Math.floor(templateRows.length / 2) * gridGapValue;

      let rowsSum = 0;

      templateRows.forEach((row, i) => {
        if (i % 2 === 0) {
          rowsSum += row;
        }
      });

      const desiredRowsSum = gridHeight - rowsGapSum;

      const rowsRatio = desiredRowsSum / rowsSum;

      templateRows.forEach((row, i) => {
        if (i % 2 === 0) {
          templateRows[i] = row * rowsRatio;
        }
      });

      const updatedTemplateRows = templateRows.map((row) => `${row}px`);

      setGridStyleRows({
        gridTemplateRows: updatedTemplateRows.join(" "),
      });
    }

    //if widthChanged
    if (previousGridWidth !== gridWidth) {
      const templateColumns = divStyles
        .getPropertyValue("grid-template-columns")
        .split(" ")
        .map((col) => parseFloat(col.replace("px", "")));

      const columnsGapSum =
        Math.floor(templateColumns.length / 2) * gridGapValue;

      let columnsSum = 0;

      templateColumns.forEach((col, i) => {
        if (i % 2 === 0) {
          columnsSum += col;
        }
      });

      const desiredColumnsSum = gridWidth - columnsGapSum;

      const columnsRatio = desiredColumnsSum / columnsSum;

      templateColumns.forEach((col, i) => {
        if (i % 2 === 0) {
          templateColumns[i] = col * columnsRatio;
        }
      });

      const updatedTemplateColumns = templateColumns.map((col) => `${col}px`);

      setGridStyleColumns({
        gridTemplateColumns: updatedTemplateColumns.join(" "),
      });
    }

    previousGridHeight = gridHeight;
    previousGridWidth = gridWidth;
  });

  const handleDragStart = (e) => {
    if (
      window.innerWidth <= styleThresholds.width ||
      window.innerHeight <= styleThresholds.height
    ) {
      setDragCell(null);
      setDragStart(null);
      return;
    }

    const cell = e.target;
    const className = Array.from(cell.classList).join(" ");

    if (!className.includes("drag")) return;

    const isMouse = e.type === "mousedown" || e.type === "dragstart";

    if (isMouse) {
      // Create a transparent image to prevent the default ghost image
      const transparentImage = new Image();
      transparentImage.src =
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAwAB/1IZ9lUAAAAASUVORK5CYII="; // Transparent 1x1 PNG

      // Set the drag image to the transparent image
      e.dataTransfer.setDragImage(transparentImage, 0, 0);
    }

    const grid = gridRef.current;
    const divStyles = window.getComputedStyle(grid);

    const templateRows = divStyles
      .getPropertyValue("grid-template-rows")
      .split(" ")
      .map((row) => parseFloat(row.replace("px", "")));

    const templateColumns = divStyles
      .getPropertyValue("grid-template-columns")
      .split(" ")
      .map((col) => parseFloat(col.replace("px", "")));

    const cellRect = cell.getBoundingClientRect();
    const gridRect = grid.getBoundingClientRect();

    const offsetY = cellRect.top - gridRect.top;
    const offsetX = cellRect.left - gridRect.left;

    let ySum = 0;
    let xSum = 0;

    let rowIndex = -1;
    let columnIndex = -1;

    /*let draggedCellType;
    const draggedCellClasses = cell.classList;
    console.log(draggedCellClasses);

    if(draggedCellClasses.contains("wide")){
      draggedCellType = 0;
    } else if(draggedCellClasses.contains("tall")){
      draggedCellType = 1;
    } else if(draggedCellClasses.contains("square")){
      draggedCellType = 2;
    } else return;*/

    //find dragged cell's index
    for (let i = 0; i < templateRows.length; i++) {
      if (templateRows[i] === gridGapValue) {
        if (Math.abs(ySum - offsetY) < gridGapValue / 2) {
          rowIndex = i;
          break;
        }
      }
      ySum += templateRows[i];
    }
    for (let i = 0; i < templateColumns.length; i++) {
      if (templateColumns[i] === gridGapValue) {
        if (Math.abs(xSum - offsetX) < gridGapValue / 2) {
          columnIndex = i;
          break;
        }
      }
      xSum += templateColumns[i];
    }

    //set drag info

    setDragCell({
      y: rowIndex,
      x: columnIndex,
    });

    setGridTemplateStart({
      x: templateColumns,
      y: templateRows,
    });

    setDragStart({
      x: isMouse ? e.clientX : e.touches[0].clientX,
      y: isMouse ? e.clientY : e.touches[0].clientY,
    });
  };

  const handleDrag = (e) => {
    if (!dragCell || !dragStart) return;
    if (e.clientY <= 0 || e.clientX <= 0) return;
    if (!gridTemplateStart || !gridTemplateStart.x || !gridTemplateStart.y){
      //console.log('gridTemplateStart');
      //console.log(gridTemplateStart);
      return;
    } 

    const isMouse = e.type === "mousemove" || e.type === "drag";

    const minimumCellSize = gridGapValue * 2;

    const updateTemplate = (template, index, difference) => {
      if(!template) return;
      let minValueFix = 0;

      let sizeLeftTracker = [];

      let toSubtract = [];
      let toSubtractIndex = 0;

      if (difference < 0) {
        //calculate size left before a cell is at minimum
        for (let i = 0; i < index; i++) {
          if (i % 2 === 0) {
            sizeLeftTracker.push(template[i] - minimumCellSize);
          }
        }

        //calculate values to subtract depending on cell proportion
        const totalToSubtract = sizeLeftTracker.reduce((a, b) => a + b, 0);

        for (let i = 0; i < sizeLeftTracker.length; i++) {
          toSubtract.push((sizeLeftTracker[i] / totalToSubtract) * difference);
        }

        //subtract values to cells
        for (let i = 0; i < index; i++) {
          if (i % 2 === 0) {
            template[i] += toSubtract[toSubtractIndex];
            if (template[i] < minimumCellSize) {
              minValueFix += minimumCellSize - template[i];
              template[i] = minimumCellSize;
            }
            toSubtractIndex++;
          }
        }

        //add to next cell
        template[index + 1] -= difference + minValueFix;
      } else {
        //calculate size left before a cell is at minimum
        for (let i = index; i < template.length; i++) {
          if (i % 2 === 0) {
            sizeLeftTracker.push(template[i] - minimumCellSize);
          }
        }

        //calculate values to subtract depending on cell proportion
        const totalToSubtract = sizeLeftTracker.reduce((a, b) => a + b, 0);

        for (let i = 0; i < sizeLeftTracker.length; i++) {
          toSubtract.push((sizeLeftTracker[i] / totalToSubtract) * difference);
        }

        //subtract values to cells
        for (let i = index; i < template.length; i++) {
          if (i % 2 === 0) {
            template[i] -= toSubtract[toSubtractIndex];
            if (template[i] < minimumCellSize) {
              minValueFix += minimumCellSize - template[i];
              template[i] = minimumCellSize;
            }
            toSubtractIndex++;
          }
        }

        //add to next cell
        template[index - 1] += difference - minValueFix;
      }

      return template.map((value) => `${value}px`);
    };

    if (dragCell.y !== -1) {
      const currentY = isMouse ? e.clientY : e.touches[0].clientY;
      const yDifference = currentY - dragStart.y;
      const updatedRows = updateTemplate(
        structuredClone(gridTemplateStart.y),
        dragCell.y,
        yDifference
      );
      setGridStyleRows({ gridTemplateRows: updatedRows.join(" ") });
    }

    if (dragCell.x !== -1) {
      const currentX = isMouse ? e.clientX : e.touches[0].clientX;
      const xDifference = currentX - dragStart.x;
      const updatedColumns = updateTemplate(
        structuredClone(gridTemplateStart.x),
        dragCell.x,
        xDifference
      );
      setGridStyleColumns({ gridTemplateColumns: updatedColumns.join(" ") });
    }
  };

  const handleDragEnd = (e) => {
    setDragCell(null);
    setDragStart(null);
  };

  const handleGridAnimationEnd = () => {
    if (gridIsAnimating) {
      setGridIsAnimating(false);
    }
  };

  useEffect(() => {
    if (pageIsExiting) {
      handlePageHasExited();
    } else if (pageIsEntering) {
      handlePageHasEntered();
    }
  }, [onAppTransitionEnd]);

  useEffect(() => {
    const handleDragOver = (event) => {
      event.preventDefault(); // This prevents the default behavior (e.g., disallowing drop)
    };
    document.addEventListener("dragover", handleDragOver);

    const resizeObserver = new ResizeObserver(() => {
      updateGridValues();
    });

    const grid = gridRef.current;
    resizeObserver.observe(grid);

    updateGridValues();

    return () => {
      document.removeEventListener("dragover", handleDragOver);
      resizeObserver.disconnect();
    };
  }, []);

  const handleHeaderPressed = (workPressed) => {
    if (isWork === workPressed) {
      //pick random work?
      return;
    }

    setPageIsExiting(true);

    if (isWork) setAppTransitionClass("exitWork");
    else setAppTransitionClass("exitAbout");
  };

  const handlePageHasExited = () => {
    setPageIsExiting(false);
    setPageIsEntering(true);

    if (isWork) {
      setAppTransitionClass("enterAbout");
      navigate("/about");
    } else {
      setAppTransitionClass("enterWork");
      navigate("/work");
    }
  };

  const handlePageHasEntered = () => {
    setPageIsEntering(false);
    //setAppTransitionClass("");
  };

  return (
    <div
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      onTouchStart={handleDragStart}
      onTouchMove={handleDrag}
      onTouchEnd={handleDragEnd}
      style={{ ...gridStyleRows, ...gridStyleColumns }}
      ref={gridRef}
      className={`page ${gridIsAnimating ? "transitionDiv" : ""}`} //transitionDiv
      onTransitionEnd={handleGridAnimationEnd}
      onAnimationEnd={handleGridAnimationEnd}
    >
      {isWork ? (
        workInfo ? (
          <Work
            workInfo={workInfo}
            headerPressed={handleHeaderPressed}
            setAppWorkColours={setAppWorkColours}
            setLastProjectIndex={setLastProjectIndex}
            lastProjectIndex={lastProjectIndex}
          />
        ) : (
          <LoadingAnimation />
        )
      ) : (
        <About headerPressed={handleHeaderPressed} />
      )}
    </div>
  );
}

export default Page;
